/**
 * Klaviyo custom "Added to Cart" (API metric) for abandoned-cart flows.
 * Prestige used `.klaviyo-adc` + setTimeout; Horizon listens to CartAddEvent after a real cart mutation.
 *
 * Payload shape must stay raw Ajax Cart API (`items`, `$value`) — do not remap field names.
 * @see docs/INTERNAL.md → Klaviyo Added to Cart (API metric)
 */
import { ThemeEvents, CartAddEvent } from '@theme/events';

/** Sources that emit CartAddEvent but are not real adds (or qty decreases) */
const EXCLUDED_SOURCES = new Set(['quick-order-remove-all', 'quick-order-quantity']);

/**
 * Queue-safe track: works with Proxy stub (`.track`) and array stub (`.push`).
 * @param {string} name
 * @param {Record<string, unknown>} properties
 */
function trackKlaviyoEvent(name, properties) {
  const klaviyo = window.klaviyo;

  if (!klaviyo) return;

  if (typeof klaviyo.track === 'function') {
    klaviyo.track(name, properties);
    return;
  }

  if (typeof klaviyo.push === 'function') {
    klaviyo.push(['track', name, properties]);
  }
}

/**
 * Full cart.js / cart/change.js / cart/update.js shape (not /cart/add.js partial).
 * @param {any} resource
 * @returns {boolean}
 */
function isFullCartResource(resource) {
  return Boolean(
    resource &&
      typeof resource === 'object' &&
      typeof resource.item_count === 'number' &&
      Array.isArray(resource.items)
  );
}

/**
 * @returns {Promise<object | null>}
 */
async function fetchCart() {
  try {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) return null;

    return await res.json();
  } catch {
    return null;
  }
}

/**
 * @param {Event} event
 */
async function trackAddedToCart(event) {
  if (!(event instanceof CartAddEvent)) return;
  if (EXCLUDED_SOURCES.has(event.detail?.data?.source)) return;
  if (event.detail?.data?.didError) return;
  if (!window.klaviyo) return;

  const resource = event.detail?.resource;
  const cart = isFullCartResource(resource) ? resource : await fetchCart();

  if (!cart) return;

  trackKlaviyoEvent('Added to Cart', {
    $value: cart.total_price / 100,
    items: cart.items,
  });
}

document.addEventListener(ThemeEvents.cartUpdate, trackAddedToCart);
